import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import SuperAdminLayout from '../components/SuperAdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Cloud, CloudOff, RefreshCw, AlertTriangle, CheckCircle, Trash2, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    screen_price_per_month: 100,
    default_demo_days: 30,
    support_email: 'support@infovegg.no'
  });
  
  // Migration state
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  
  // Cleanup state
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOptions, setDeleteOptions] = useState({
    articles: false,
    messages: false,
    users: false,
    screens: false
  });

  useEffect(() => {
    fetchSettings();
    fetchMigrationStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/system-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({ ...settings, ...response.data });
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };
  
  const fetchMigrationStatus = async () => {
    setIsCheckingStatus(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/migrate/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMigrationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch migration status:', error);
    } finally {
      setIsCheckingStatus(false);
    }
  };
  
  const runMigration = async (dryRun = true) => {
    setIsMigrating(true);
    setMigrationResult(null);
    
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/admin/migrate/cloudinary-to-s3?dry_run=${dryRun}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000 // 5 minute timeout for large migrations
        }
      );
      
      setMigrationResult(response.data);
      
      if (dryRun) {
        toast.success(`Forhåndsvisning fullført: ${response.data.stats.total_found} bilder funnet`);
      } else {
        toast.success(`Migrering fullført: ${response.data.stats.migrated_success} bilder migrert`);
        // Refresh status after migration
        fetchMigrationStatus();
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast.error(error.response?.data?.detail || 'Migrering feilet');
    } finally {
      setIsMigrating(false);
    }
  };
  
  // Cleanup functions
  const scanOrphanedData = async () => {
    setIsScanning(true);
    setScanResult(null);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/cleanup/scan`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScanResult(response.data);
      if (response.data.summary.total_orphaned === 0) {
        toast.success('Ingen foreldreløs data funnet!');
      } else {
        toast.info(`Funnet ${response.data.summary.total_orphaned} foreldreløse elementer`);
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Kunne ikke skanne databasen');
    } finally {
      setIsScanning(false);
    }
  };
  
  const deleteOrphanedData = async () => {
    if (!deleteOptions.articles && !deleteOptions.messages && !deleteOptions.users && !deleteOptions.screens) {
      toast.error('Velg minst én type data å slette');
      return;
    }
    
    if (!window.confirm('Er du sikker på at du vil slette valgte foreldreløse data? Dette kan ikke angres.')) {
      return;
    }
    
    setIsDeleting(true);
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (deleteOptions.articles) params.append('delete_articles', 'true');
      if (deleteOptions.messages) params.append('delete_messages', 'true');
      if (deleteOptions.users) params.append('delete_users', 'true');
      if (deleteOptions.screens) params.append('delete_screens', 'true');
      
      const response = await axios.post(`${API}/admin/cleanup/delete?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Slettet ${response.data.total_deleted} elementer`);
      // Refresh scan
      scanOrphanedData();
      // Reset checkboxes
      setDeleteOptions({ articles: false, messages: false, users: false, screens: false });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Kunne ikke slette data');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/system-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Innstillinger lagret!');
    } catch (error) {
      toast.error('Kunne ikke lagre innstillinger');
    }
  };

  return (
    <SuperAdminLayout>
      <div className="admin-header">
        <h1>{t('superAdmin.settings.title')}</h1>
      </div>

      <div style={{ maxWidth: '800px' }}>
        {/* Database Cleanup Section */}
        <div className="admin-table" style={{ padding: '2rem', marginBottom: '2rem' }}>
          <h2 style={{ color: 'white', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Trash2 size={24} color="#f87171" />
            Databaseopprydding
          </h2>
          <p style={{ color: '#9ca3af', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Finn og slett foreldreløs data som refererer til organisasjoner som ikke lenger eksisterer.
          </p>
          
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
            <button 
              className="btn btn-secondary" 
              onClick={scanOrphanedData}
              disabled={isScanning}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Search size={18} className={isScanning ? 'spin' : ''} />
              {isScanning ? 'Skanner...' : 'Skann database'}
            </button>
          </div>
          
          {scanResult && (
            <div style={{ 
              background: 'rgba(0, 0, 0, 0.3)', 
              borderRadius: '8px', 
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ color: '#50E3C2', marginBottom: '1rem', fontSize: '1.1rem' }}>
                Skanneresultat
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ 
                  background: scanResult.summary.orphaned_articles_count > 0 ? 'rgba(248, 113, 113, 0.1)' : 'rgba(80, 227, 194, 0.1)',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: `1px solid ${scanResult.summary.orphaned_articles_count > 0 ? 'rgba(248, 113, 113, 0.3)' : 'rgba(80, 227, 194, 0.3)'}`
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: scanResult.summary.orphaned_articles_count > 0 ? '#f87171' : '#50E3C2' }}>
                    {scanResult.summary.orphaned_articles_count}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Foreldreløse artikler</div>
                </div>
                
                <div style={{ 
                  background: scanResult.summary.orphaned_messages_count > 0 ? 'rgba(248, 113, 113, 0.1)' : 'rgba(80, 227, 194, 0.1)',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: `1px solid ${scanResult.summary.orphaned_messages_count > 0 ? 'rgba(248, 113, 113, 0.3)' : 'rgba(80, 227, 194, 0.3)'}`
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: scanResult.summary.orphaned_messages_count > 0 ? '#f87171' : '#50E3C2' }}>
                    {scanResult.summary.orphaned_messages_count}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Foreldreløse meldinger</div>
                </div>
                
                <div style={{ 
                  background: scanResult.summary.orphaned_users_count > 0 ? 'rgba(248, 113, 113, 0.1)' : 'rgba(80, 227, 194, 0.1)',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: `1px solid ${scanResult.summary.orphaned_users_count > 0 ? 'rgba(248, 113, 113, 0.3)' : 'rgba(80, 227, 194, 0.3)'}`
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: scanResult.summary.orphaned_users_count > 0 ? '#f87171' : '#50E3C2' }}>
                    {scanResult.summary.orphaned_users_count}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Foreldreløse brukere</div>
                </div>
                
                <div style={{ 
                  background: scanResult.summary.orphaned_screens_count > 0 ? 'rgba(248, 113, 113, 0.1)' : 'rgba(80, 227, 194, 0.1)',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: `1px solid ${scanResult.summary.orphaned_screens_count > 0 ? 'rgba(248, 113, 113, 0.3)' : 'rgba(80, 227, 194, 0.3)'}`
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: scanResult.summary.orphaned_screens_count > 0 ? '#f87171' : '#50E3C2' }}>
                    {scanResult.summary.orphaned_screens_count}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Foreldreløse skjermer</div>
                </div>
              </div>
              
              {scanResult.summary.total_orphaned > 0 && (
                <>
                  <h4 style={{ color: 'white', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                    Velg hva som skal slettes:
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={deleteOptions.articles}
                        onChange={(e) => setDeleteOptions({...deleteOptions, articles: e.target.checked})}
                        disabled={scanResult.summary.orphaned_articles_count === 0}
                      />
                      Artikler ({scanResult.summary.orphaned_articles_count})
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={deleteOptions.messages}
                        onChange={(e) => setDeleteOptions({...deleteOptions, messages: e.target.checked})}
                        disabled={scanResult.summary.orphaned_messages_count === 0}
                      />
                      Meldinger ({scanResult.summary.orphaned_messages_count})
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={deleteOptions.users}
                        onChange={(e) => setDeleteOptions({...deleteOptions, users: e.target.checked})}
                        disabled={scanResult.summary.orphaned_users_count === 0}
                      />
                      Brukere ({scanResult.summary.orphaned_users_count})
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={deleteOptions.screens}
                        onChange={(e) => setDeleteOptions({...deleteOptions, screens: e.target.checked})}
                        disabled={scanResult.summary.orphaned_screens_count === 0}
                      />
                      Skjermer ({scanResult.summary.orphaned_screens_count})
                    </label>
                  </div>
                  
                  <button 
                    className="btn btn-danger" 
                    onClick={deleteOrphanedData}
                    disabled={isDeleting}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Trash2 size={18} />
                    {isDeleting ? 'Sletter...' : 'Slett valgte'}
                  </button>
                  
                  {/* Show details of orphaned items */}
                  {(scanResult.orphaned_articles.length > 0 || scanResult.orphaned_messages.length > 0 || scanResult.orphaned_users.length > 0) && (
                    <details style={{ marginTop: '1rem' }}>
                      <summary style={{ color: '#9ca3af', cursor: 'pointer', fontSize: '0.9rem' }}>
                        Vis detaljer om foreldreløse elementer
                      </summary>
                      <div style={{ marginTop: '1rem', maxHeight: '300px', overflowY: 'auto' }}>
                        {scanResult.orphaned_articles.length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <h5 style={{ color: '#f87171', marginBottom: '0.5rem' }}>Artikler:</h5>
                            {scanResult.orphaned_articles.map((a, i) => (
                              <div key={i} style={{ fontSize: '0.85rem', color: '#9ca3af', paddingLeft: '1rem' }}>
                                • {a.title} (org: {a.organization_id})
                              </div>
                            ))}
                          </div>
                        )}
                        {scanResult.orphaned_messages.length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <h5 style={{ color: '#f87171', marginBottom: '0.5rem' }}>Meldinger:</h5>
                            {scanResult.orphaned_messages.map((m, i) => (
                              <div key={i} style={{ fontSize: '0.85rem', color: '#9ca3af', paddingLeft: '1rem' }}>
                                • {m.title} (org: {m.organization_id})
                              </div>
                            ))}
                          </div>
                        )}
                        {scanResult.orphaned_users.length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <h5 style={{ color: '#f87171', marginBottom: '0.5rem' }}>Brukere:</h5>
                            {scanResult.orphaned_users.map((u, i) => (
                              <div key={i} style={{ fontSize: '0.85rem', color: '#9ca3af', paddingLeft: '1rem' }}>
                                • {u.email} - {u.name} (org: {u.organization_id})
                              </div>
                            ))}
                          </div>
                        )}
                        {scanResult.orphaned_screens.length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <h5 style={{ color: '#f87171', marginBottom: '0.5rem' }}>Skjermer:</h5>
                            {scanResult.orphaned_screens.map((s, i) => (
                              <div key={i} style={{ fontSize: '0.85rem', color: '#9ca3af', paddingLeft: '1rem' }}>
                                • {s.screen_name} (org: {s.organization_id})
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </>
              )}
              
              {scanResult.summary.total_orphaned === 0 && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  color: '#50E3C2',
                  background: 'rgba(80, 227, 194, 0.1)',
                  padding: '1rem',
                  borderRadius: '6px'
                }}>
                  <CheckCircle size={20} />
                  Databasen er ren - ingen foreldreløs data funnet!
                </div>
              )}
            </div>
          )}
          
          <p style={{ color: '#6b7280', fontSize: '0.8rem' }}>
            <strong>Tips:</strong> Klikk "Skann database" for å se hva som vil bli slettet før du faktisk sletter noe.
          </p>
        </div>
        <div className="admin-table" style={{ padding: '2rem', marginBottom: '2rem' }}>
          <h2 style={{ color: 'white', marginBottom: '1.5rem' }}>{t('superAdmin.dashboard.paidScreens')}</h2>
          
          <div className="form-group">
            <label>{t('superAdmin.dashboard.perScreenPerMonth')}</label>
            <input
              type="number"
              value={settings.screen_price_per_month}
              onChange={(e) => setSettings({...settings, screen_price_per_month: parseInt(e.target.value)})}
              min="0"
              style={{ color: 'white' }}
            />
            <small style={{ color: '#a0a0a0', marginTop: '0.5rem', display: 'block' }}>
              {t('superAdmin.settings.generalSettings')}
            </small>
          </div>
        </div>

        <div className="admin-table" style={{ padding: '2rem', marginBottom: '2rem' }}>
          <h2 style={{ color: 'white', marginBottom: '1.5rem' }}>{t('superAdmin.organizations.demo')}</h2>
          
          <div className="form-group">
            <label>{t('superAdmin.organizations.demo')}</label>
            <input
              type="number"
              value={settings.default_demo_days}
              onChange={(e) => setSettings({...settings, default_demo_days: parseInt(e.target.value)})}
              min="1"
              max="365"
              style={{ color: 'white' }}
            />
            <small style={{ color: '#a0a0a0', marginTop: '0.5rem', display: 'block' }}>
              {t('superAdmin.organizations.demo')}
            </small>
          </div>
        </div>

        <div className="admin-table" style={{ padding: '2rem', marginBottom: '2rem' }}>
          <h2 style={{ color: 'white', marginBottom: '1.5rem' }}>{t('customer.support.contactSupport')}</h2>
          
          <div className="form-group">
            <label>{t('superAdmin.settings.supportEmail')}</label>
            <input
              type="email"
              value={settings.support_email}
              onChange={(e) => setSettings({...settings, support_email: e.target.value})}
              style={{ color: 'white' }}
            />
          </div>
        </div>
        
        {/* Cloudinary to AWS S3 Migration Section */}
        <div className="admin-table" style={{ padding: '2rem', marginBottom: '2rem', border: '1px solid #50E3C2' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <Cloud size={24} color="#50E3C2" />
            <h2 style={{ color: 'white', margin: 0 }}>Bildemigrering: Cloudinary → AWS S3</h2>
          </div>
          
          <p style={{ color: '#a0a0a0', marginBottom: '1.5rem' }}>
            Migrer alle bilder fra Cloudinary til AWS S3. Bilder som allerede er på AWS vil bli hoppet over.
          </p>
          
          {/* Migration Status */}
          <div style={{ 
            background: 'rgba(0, 0, 0, 0.3)', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1.5rem' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: '#50E3C2', margin: 0, fontSize: '1rem' }}>Nåværende status</h3>
              <button
                onClick={fetchMigrationStatus}
                disabled={isCheckingStatus}
                style={{
                  background: 'transparent',
                  border: '1px solid #50E3C2',
                  color: '#50E3C2',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  cursor: isCheckingStatus ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.875rem'
                }}
              >
                <RefreshCw size={14} className={isCheckingStatus ? 'spin' : ''} />
                Oppdater
              </button>
            </div>
            
            {migrationStatus ? (
              <div>
                {migrationStatus.summary.migration_complete ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#22c55e', marginBottom: '1rem' }}>
                    <CheckCircle size={20} />
                    <span style={{ fontWeight: 'bold' }}>Migrering fullført! Alle bilder er på AWS S3.</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#f59e0b', marginBottom: '1rem' }}>
                    <AlertTriangle size={20} />
                    <span style={{ fontWeight: 'bold' }}>
                      {migrationStatus.summary.total_cloudinary_urls} bilder gjenstår på Cloudinary
                    </span>
                  </div>
                )}
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '6px' }}>
                    <div style={{ color: '#f59e0b', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {migrationStatus.summary.total_cloudinary_urls}
                    </div>
                    <div style={{ color: '#a0a0a0', fontSize: '0.875rem' }}>Cloudinary</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '6px' }}>
                    <div style={{ color: '#22c55e', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {migrationStatus.summary.total_aws_urls}
                    </div>
                    <div style={{ color: '#a0a0a0', fontSize: '0.875rem' }}>AWS S3</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(80, 227, 194, 0.1)', borderRadius: '6px' }}>
                    <div style={{ color: '#50E3C2', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {migrationStatus.summary.total_cloudinary_urls + migrationStatus.summary.total_aws_urls}
                    </div>
                    <div style={{ color: '#a0a0a0', fontSize: '0.875rem' }}>Totalt</div>
                  </div>
                </div>
                
                {/* Detailed breakdown */}
                <details style={{ marginTop: '1rem' }}>
                  <summary style={{ color: '#a0a0a0', cursor: 'pointer', fontSize: '0.875rem' }}>
                    Vis detaljer per samling
                  </summary>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#a0a0a0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem' }}>Samling</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem', color: '#f59e0b' }}>Cloudinary</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem', color: '#22c55e' }}>AWS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(migrationStatus.status.cloudinary_urls).map(collection => (
                          <tr key={collection} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <td style={{ padding: '0.5rem' }}>{collection}</td>
                            <td style={{ textAlign: 'right', padding: '0.5rem', color: '#f59e0b' }}>
                              {migrationStatus.status.cloudinary_urls[collection]}
                            </td>
                            <td style={{ textAlign: 'right', padding: '0.5rem', color: '#22c55e' }}>
                              {migrationStatus.status.aws_urls[collection] || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              </div>
            ) : isCheckingStatus ? (
              <p style={{ color: '#a0a0a0' }}>Henter status...</p>
            ) : (
              <p style={{ color: '#a0a0a0' }}>Klikk "Oppdater" for å sjekke status</p>
            )}
          </div>
          
          {/* Migration Result */}
          {migrationResult && (
            <div style={{ 
              background: migrationResult.dry_run ? 'rgba(59, 130, 246, 0.1)' : 'rgba(34, 197, 94, 0.1)', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1.5rem',
              border: `1px solid ${migrationResult.dry_run ? '#3b82f6' : '#22c55e'}`
            }}>
              <h4 style={{ color: migrationResult.dry_run ? '#3b82f6' : '#22c55e', margin: '0 0 0.5rem 0' }}>
                {migrationResult.dry_run ? 'Forhåndsvisning' : 'Migrering fullført'}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.875rem', color: '#a0a0a0' }}>
                <div>Funnet: <span style={{ color: 'white' }}>{migrationResult.stats.total_found}</span></div>
                <div>Allerede migrert: <span style={{ color: 'white' }}>{migrationResult.stats.already_migrated}</span></div>
                <div>Migrert: <span style={{ color: '#22c55e' }}>{migrationResult.stats.migrated_success}</span></div>
                <div>Feilet: <span style={{ color: migrationResult.stats.migrated_failed > 0 ? '#ef4444' : 'white' }}>{migrationResult.stats.migrated_failed}</span></div>
              </div>
              {migrationResult.stats.errors && migrationResult.stats.errors.length > 0 && (
                <div style={{ marginTop: '0.5rem', color: '#ef4444', fontSize: '0.8rem' }}>
                  Feil: {migrationResult.stats.errors.join(', ')}
                </div>
              )}
            </div>
          )}
          
          {/* Migration Buttons */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => runMigration(true)}
              disabled={isMigrating || (migrationStatus?.summary?.migration_complete)}
              style={{
                background: 'transparent',
                border: '1px solid #3b82f6',
                color: '#3b82f6',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: isMigrating || migrationStatus?.summary?.migration_complete ? 'not-allowed' : 'pointer',
                opacity: isMigrating || migrationStatus?.summary?.migration_complete ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500'
              }}
            >
              <CloudOff size={18} />
              {isMigrating ? 'Kjører...' : 'Forhåndsvis migrering'}
            </button>
            
            <button
              onClick={() => {
                if (window.confirm('Er du sikker på at du vil migrere alle bilder fra Cloudinary til AWS S3? Dette kan ta noen minutter.')) {
                  runMigration(false);
                }
              }}
              disabled={isMigrating || (migrationStatus?.summary?.migration_complete)}
              style={{
                background: migrationStatus?.summary?.migration_complete ? '#6b7280' : '#22c55e',
                border: 'none',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: isMigrating || migrationStatus?.summary?.migration_complete ? 'not-allowed' : 'pointer',
                opacity: isMigrating || migrationStatus?.summary?.migration_complete ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500'
              }}
            >
              <Cloud size={18} />
              {isMigrating ? 'Migrerer...' : 'Start migrering'}
            </button>
          </div>
          
          <p style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '1rem' }}>
            <strong>Tips:</strong> Klikk "Forhåndsvis migrering" først for å se hvor mange bilder som vil bli migrert uten å gjøre endringer.
          </p>
        </div>

        <button className="btn btn-primary" onClick={handleSave}>
          <Save size={20} /> {t('superAdmin.settings.save')}
        </button>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminSettings;
